/**
 * DecisionFlow — DealSplitManager
 * data-fit LLC · Confidential
 * Domain: §46 Deal Splits (m074)
 * Spec authority: decisionflow_deal_splits_spec_v1_0.md §C + §D
 * API Contract: api_contract_v1_31.md §46
 * Wizard substages:
 *   split_plan_definition · split_position_assignment · split_allocation_validation
 * Feature gate: feature.tier.deal_splits — enterprise only.
 *   403 SPLIT_FEATURE_UNAVAILABLE → GateStatusBanner.
 * tenant_super_admin excluded (NN-36).
 * State derived from approved_by / submitted_at — no plan_status column on split_plan table.
 * onReject={null} — reject endpoint not confirmed for §46.
 * split_pct allocations must sum to exactly 1.0000; primary position must be included.
 * person_uuid never accepted as split input (NN-38).
 * Path: ui/src/components/tenant-admin/DealSplitManager.jsx
 *
 * S312 — OI-S307-UI-THEME: Full CSS token compliance pass.
 *   StatusPill prop corrected: state= → status= (shared StatusPill expects `status` prop).
 *   inputClass updated: added focus:ring-2 focus:ring-[rgb(var(--color-focus-ring))] token.
 *   All other token usage already compliant from prior pass.
 *   No logic, workflow, or structural changes.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../auth/AuthContext.jsx';
import { useFeatures } from '../../auth/FeatureContext.jsx';
import { apiGet, apiPost } from '../../api/client';
import ApprovalPanel from '../shared/ApprovalPanel.jsx';
import GateStatusBanner from '../shared/GateStatusBanner.jsx';
import StatusPill from '../shared/StatusPill.jsx';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TABS = [
  { key: 'plans',       label: 'Split Plans'         },
  { key: 'assignments', label: 'Position Assignments' },
  { key: 'allocations', label: 'Allocation Validation' },
];

const EMPTY_PLAN_FORM = {
  split_plan_code:          '',
  split_plan_name:          '',
  max_split_participants:   '5',
  requires_full_allocation: true,
  allow_single_rep_no_split: true,
};

const EMPTY_ASSIGNMENT_FORM = {
  split_plan_id:   '',
  position_id:     '',
  fiscal_period_id: '',
};

const EMPTY_ALLOCATION_FORM = {
  credit_transaction_id: '',
  split_plan_id:         '',
  rows: [
    { position_id: '', split_sequence: 1, split_pct: '' },
    { position_id: '', split_sequence: 2, split_pct: '' },
  ],
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function deriveState(plan) {
  if (plan.approved_by)                         return 'approved';
  if (plan.submitted_at || plan.submitted_by)   return 'pending_approval';
  return 'draft';
}

function getApiErrorCode(error) {
  return error?.code || null;
}

function getErrorMessage(error, fallback) {
  return error?.message || fallback;
}

function sumSplitPct(rows) {
  return rows.reduce((sum, row) => {
    const val = parseFloat(row.split_pct);
    return sum + (Number.isFinite(val) ? val : 0);
  }, 0);
}

// ---------------------------------------------------------------------------
// Shared class strings — M040 token pattern
// ---------------------------------------------------------------------------

const thClass      = 'px-3 py-2 text-left text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))] bg-[rgb(var(--color-surface-muted))] border-b border-[rgb(var(--color-border))]';
const tdClass      = 'px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] border-b border-[rgb(var(--color-border))]';
const btnPrimary   = 'rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-sm font-medium text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50 disabled:cursor-not-allowed';
const btnSecondary = 'rounded-lg bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-sm font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50 disabled:cursor-not-allowed';
const inputClass   = 'mt-1 block w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-3 py-2 text-tok-sm focus:outline-none focus:ring-2 focus:ring-[rgb(var(--color-focus-ring))]';
const labelClass   = 'flex flex-col text-tok-sm font-medium text-[rgb(var(--color-text))]';

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function DealSplitManager({ mode, period, wizardSteps, embedded = false, planId, planComponentId, fiscalPeriodId, readOnly = false }) {
  const { user } = useAuth();
  const { hasPlanType } = useFeatures();

  if (!hasPlanType('feature.plan.splits')) {
    return <GateStatusBanner code="PLAN_TYPE_NOT_ENTITLED" />;
  }

  // Note: DealSplitManager is plan-level, not component-scoped.
  // planComponentId prop accepted for API consistency but unused.

  const [activeTab, setActiveTab]         = useState('plans');
  const [featureUnavailable, setFeatureUnavailable] = useState(false);
  const [toast, setToast]                 = useState(null);

  // Plans state
  const [plans, setPlans]                 = useState([]);
  const [plansLoading, setPlansLoading]   = useState(false);
  const [plansError, setPlansError]       = useState('');
  const [selectedPlan, setSelectedPlan]   = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showPlanForm, setShowPlanForm]   = useState(false);
  const [planForm, setPlanForm]           = useState(EMPTY_PLAN_FORM);
  const [planSaving, setPlanSaving]       = useState(false);
  const [planFormError, setPlanFormError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError]     = useState('');

  // Assignments state
  const [assignments, setAssignments]               = useState([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [assignmentsError, setAssignmentsError]     = useState('');
  const [assignForm, setAssignForm]                 = useState(EMPTY_ASSIGNMENT_FORM);
  const [assignSaving, setAssignSaving]             = useState(false);
  const [assignFormError, setAssignFormError]       = useState('');

  // Positions reference (for assignment + allocation pickers)
  const [positions, setPositions]         = useState([]);

  // Allocations state
  const [allocForm, setAllocForm]         = useState(EMPTY_ALLOCATION_FORM);
  const [allocSaving, setAllocSaving]     = useState(false);
  const [allocError, setAllocError]       = useState('');
  const [allocResult, setAllocResult]     = useState(null);

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  const showToast = useCallback((message) => {
    setToast(message);
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  // ---------------------------------------------------------------------------
  // Data fetching
  // ---------------------------------------------------------------------------

  const loadPlans = useCallback(async () => {
    if (!user) return;
    setPlansLoading(true);
    setPlansError('');
    try {
      // C7 — S689 plan_id scoping: filter split plans to this plan when planId injected.
      const data = await apiGet(`/api/v1/split-plans${planId != null ? '?plan_id=' + planId : ''}`);
      setPlans(Array.isArray(data) ? data : data?.data || []);
    } catch (error) {
      if (getApiErrorCode(error) === 'SPLIT_FEATURE_UNAVAILABLE') {
        setFeatureUnavailable(true);
        return;
      }
      setPlansError(getErrorMessage(error, 'Unable to load split plans.'));
    } finally {
      setPlansLoading(false);
    }
  }, [user]);

  const loadPlanDetail = useCallback(async (planId) => {
    setDetailLoading(true);
    setActionError('');
    try {
      const data = await apiGet(`/api/v1/split-plans/${planId}`);
      setSelectedPlan(data);
    } catch (error) {
      setActionError(getErrorMessage(error, 'Unable to load split plan detail.'));
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const loadAssignments = useCallback(async () => {
    if (!user) return;
    setAssignmentsLoading(true);
    setAssignmentsError('');
    try {
      const periodId = period?.fiscal_period_id;
      const url = periodId
        ? `/api/v1/split-plan-assignments?fiscal_period_id=${periodId}`
        : '/api/v1/split-plan-assignments';
      const data = await apiGet(url);
      setAssignments(Array.isArray(data) ? data : data?.data || []);
    } catch (error) {
      setAssignmentsError(getErrorMessage(error, 'Unable to load split plan assignments.'));
    } finally {
      setAssignmentsLoading(false);
    }
  }, [user, period]);

  const loadPositions = useCallback(async () => {
    if (!user) return;
    try {
      const data = await apiGet('/api/v1/positions');
      setPositions(Array.isArray(data) ? data : data?.positions || data?.data || []);
    } catch {
      // non-blocking — positions are reference data
    }
  }, [user]);

  useEffect(() => {
    loadPlans();
    loadPositions();
  }, [loadPlans, loadPositions]);

  useEffect(() => {
    if (activeTab === 'assignments') loadAssignments();
  }, [activeTab, loadAssignments]);

  // ---------------------------------------------------------------------------
  // Approved plans (for assignment picker)
  // ---------------------------------------------------------------------------

  const approvedPlans = useMemo(
    () => plans.filter((p) => deriveState(p) === 'approved'),
    [plans]
  );

  const positionNameById = useMemo(
    () => positions.reduce((acc, p) => {
      acc[String(p.position_id)] = p.full_name || `Position ${p.position_id}`;
      return acc;
    }, {}),
    [positions]
  );

  const planNameById = useMemo(
    () => plans.reduce((acc, p) => {
      acc[String(p.split_plan_id)] = p.split_plan_name || p.split_plan_code;
      return acc;
    }, {}),
    [plans]
  );

  // ---------------------------------------------------------------------------
  // Plan form helpers
  // ---------------------------------------------------------------------------

  const updatePlanForm = useCallback((field, value) => {
    setPlanForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const resetPlanForm = useCallback(() => {
    setPlanForm(EMPTY_PLAN_FORM);
    setPlanFormError('');
  }, []);

  const validatePlanForm = useCallback(() => {
    if (!planForm.split_plan_code.trim()) return 'Plan code is required.';
    if (!planForm.split_plan_name.trim()) return 'Plan name is required.';
    const max = Number(planForm.max_split_participants);
    if (!Number.isFinite(max) || max < 2) return 'Max split participants must be at least 2.';
    return '';
  }, [planForm]);

  const buildPlanPayload = useCallback(() => ({
    split_plan_code:           planForm.split_plan_code.trim(),
    split_plan_name:           planForm.split_plan_name.trim(),
    max_split_participants:    Number(planForm.max_split_participants),
    requires_full_allocation:  planForm.requires_full_allocation,
    allow_single_rep_no_split: planForm.allow_single_rep_no_split,
  }), [planForm]);

  // ---------------------------------------------------------------------------
  // Plan actions
  // ---------------------------------------------------------------------------

  const createPlan = useCallback(async () => {
    const err = validatePlanForm();
    if (err) { setPlanFormError(err); return; }
    setPlanSaving(true);
    setPlanFormError('');
    try {
      const created = await apiPost('/api/v1/split-plans', buildPlanPayload());
      await loadPlans();
      setShowPlanForm(false);
      resetPlanForm();
      showToast('Split plan created.');
      if (created?.split_plan_id) await loadPlanDetail(created.split_plan_id);
    } catch (error) {
      setPlanFormError(getErrorMessage(error, 'Unable to create split plan.'));
    } finally {
      setPlanSaving(false);
    }
  }, [buildPlanPayload, loadPlanDetail, loadPlans, resetPlanForm, showToast, validatePlanForm]);

  const submitPlan = useCallback(async () => {
    if (!selectedPlan) return;
    setActionLoading(true);
    setActionError('');
    try {
      await apiPost(`/api/v1/split-plans/${selectedPlan.split_plan_id}/submit`, {});
      await loadPlanDetail(selectedPlan.split_plan_id);
      await loadPlans();
      showToast('Split plan submitted for approval.');
    } catch (error) {
      setActionError(getErrorMessage(error, 'Unable to submit split plan.'));
    } finally {
      setActionLoading(false);
    }
  }, [loadPlanDetail, loadPlans, selectedPlan, showToast]);

  const approvePlan = useCallback(async () => {
    if (!selectedPlan) return;
    setActionLoading(true);
    setActionError('');
    try {
      await apiPost(`/api/v1/split-plans/${selectedPlan.split_plan_id}/approve`, {});
      await loadPlanDetail(selectedPlan.split_plan_id);
      await loadPlans();
      showToast('Split plan approved.');
    } catch (error) {
      setActionError(getErrorMessage(error, 'Unable to approve split plan.'));
    } finally {
      setActionLoading(false);
    }
  }, [loadPlanDetail, loadPlans, selectedPlan, showToast]);

  // ---------------------------------------------------------------------------
  // Assignment actions
  // ---------------------------------------------------------------------------

  const updateAssignForm = useCallback((field, value) => {
    setAssignForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const validateAssignForm = useCallback(() => {
    if (!assignForm.split_plan_id)    return 'Split plan is required.';
    if (!assignForm.position_id)      return 'Position is required.';
    if (!assignForm.fiscal_period_id) return 'Fiscal period is required.';
    return '';
  }, [assignForm]);

  const createAssignment = useCallback(async () => {
    const err = validateAssignForm();
    if (err) { setAssignFormError(err); return; }
    setAssignSaving(true);
    setAssignFormError('');
    try {
      await apiPost('/api/v1/split-plan-assignments', {
        split_plan_id:   Number(assignForm.split_plan_id),
        position_id:     assignForm.position_id,
        fiscal_period_id: Number(assignForm.fiscal_period_id),
      });
      await loadAssignments();
      setAssignForm(EMPTY_ASSIGNMENT_FORM);
      showToast('Split plan assigned to position.');
    } catch (error) {
      setAssignFormError(getErrorMessage(error, 'Unable to create split plan assignment.'));
    } finally {
      setAssignSaving(false);
    }
  }, [assignForm, loadAssignments, showToast, validateAssignForm]);

  // ---------------------------------------------------------------------------
  // Allocation helpers
  // ---------------------------------------------------------------------------

  const updateAllocRow = useCallback((index, field, value) => {
    setAllocForm((prev) => {
      const rows = prev.rows.map((row, i) =>
        i === index ? { ...row, [field]: value } : row
      );
      return { ...prev, rows };
    });
  }, []);

  const addAllocRow = useCallback(() => {
    setAllocForm((prev) => ({
      ...prev,
      rows: [
        ...prev.rows,
        { position_id: '', split_sequence: prev.rows.length + 1, split_pct: '' },
      ],
    }));
  }, []);

  const removeAllocRow = useCallback((index) => {
    setAllocForm((prev) => ({
      ...prev,
      rows: prev.rows
        .filter((_, i) => i !== index)
        .map((row, i) => ({ ...row, split_sequence: i + 1 })),
    }));
  }, []);

  const allocTotal = useMemo(() => sumSplitPct(allocForm.rows), [allocForm.rows]);
  const allocTotalValid = Math.abs(allocTotal - 1.0) < 0.00005;

  const validateAllocForm = useCallback(() => {
    if (!allocForm.credit_transaction_id.trim()) return 'Credit transaction ID is required.';
    if (!allocForm.split_plan_id)                return 'Split plan is required.';
    if (allocForm.rows.length < 2)               return 'At least 2 allocation rows are required.';
    for (const row of allocForm.rows) {
      if (!row.position_id)      return 'All rows must have a position.';
      if (!row.split_pct || Number(row.split_pct) <= 0) return 'All split percentages must be greater than 0.';
    }
    if (!allocTotalValid) return `Split percentages must sum to exactly 1.0000 (current: ${allocTotal.toFixed(4)}).`;
    return '';
  }, [allocForm, allocTotal, allocTotalValid]);

  const submitAllocation = useCallback(async () => {
    const err = validateAllocForm();
    if (err) { setAllocError(err); return; }
    setAllocSaving(true);
    setAllocError('');
    try {
      const result = await apiPost('/api/v1/split-allocations', {
        credit_transaction_id: Number(allocForm.credit_transaction_id),
        split_plan_id:         Number(allocForm.split_plan_id),
        allocations: allocForm.rows.map((row) => ({
          position_id:     row.position_id,
          split_sequence:  row.split_sequence,
          split_pct:       row.split_pct,
        })),
      });
      setAllocResult(result);
      showToast('Split allocation created.');
    } catch (error) {
      setAllocError(getErrorMessage(error, 'Unable to create split allocation.'));
    } finally {
      setAllocSaving(false);
    }
  }, [allocForm, showToast, validateAllocForm]);

  // ---------------------------------------------------------------------------
  // Feature gate
  // ---------------------------------------------------------------------------

  if (featureUnavailable) {
    return <GateStatusBanner feature="Deal Splits" />;
  }

  // ---------------------------------------------------------------------------
  // Auth guard
  // ---------------------------------------------------------------------------

  if (!user) {
    return (
      <section className="flex flex-col gap-6">
        <h2 className="text-tok-xl font-semibold text-[rgb(var(--color-text))]">Deal Splits</h2>
        <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
          Authentication required.
        </div>
      </section>
    );
  }

  // ---------------------------------------------------------------------------
  // Plan detail / approval view
  // ---------------------------------------------------------------------------

  if (selectedPlan) {
    const state = deriveState(selectedPlan);
    return (
      <section className="flex flex-col gap-6">
        <header className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <button type="button" className={btnSecondary} onClick={() => { setSelectedPlan(null); setActionError(''); }}>
              ← Back to list
            </button>
            <h2 className="mt-3 text-tok-xl font-semibold text-[rgb(var(--color-text))]">
              {selectedPlan.split_plan_name}
            </h2>
            <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">
              {selectedPlan.split_plan_code} · Max {selectedPlan.max_split_participants} participants
            </p>
          </div>
          <StatusPill status={state} />
        </header>

        {actionError && (
          <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
            {actionError}
          </div>
        )}

        {/* Plan detail card */}
        <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-3 shadow-sm">
          <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Plan details</h3>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-tok-sm">
            <div>
              <dt className="text-[rgb(var(--color-text-muted))] font-medium">Max participants</dt>
              <dd className="text-[rgb(var(--color-text))]">{selectedPlan.max_split_participants}</dd>
            </div>
            <div>
              <dt className="text-[rgb(var(--color-text-muted))] font-medium">Requires full allocation</dt>
              <dd className="text-[rgb(var(--color-text))]">{selectedPlan.requires_full_allocation ? 'Yes' : 'No'}</dd>
            </div>
            <div>
              <dt className="text-[rgb(var(--color-text-muted))] font-medium">Allow single rep (no split)</dt>
              <dd className="text-[rgb(var(--color-text))]">{selectedPlan.allow_single_rep_no_split ? 'Yes' : 'No'}</dd>
            </div>
            <div>
              <dt className="text-[rgb(var(--color-text-muted))] font-medium">Version</dt>
              <dd className="text-[rgb(var(--color-text))]">{selectedPlan.plan_version ?? '—'}</dd>
            </div>
          </dl>
        </div>

        {detailLoading ? (
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Loading…</p>
        ) : (
          <ApprovalPanel
            variant="split_plan"
            currentState={state}
            submittedBy={selectedPlan.submitted_by}
            currentUserId={user?.user_id || user?.id || null}
            onSubmit={state === 'draft' ? submitPlan : null}
            onApprove={state === 'pending_approval' ? approvePlan : null}
            onReject={null}
            approvedBy={selectedPlan.approved_by}
            approvedAt={selectedPlan.approved_at}
            loading={actionLoading}
          />
        )}
      </section>
    );
  }

  // ---------------------------------------------------------------------------
  // Main tabbed view
  // ---------------------------------------------------------------------------

  return (
    <section className="flex flex-col gap-6">
      {/* Toast */}
      {toast && (
        <div role="status" className="fixed bottom-4 right-4 z-50 rounded-lg bg-[rgb(var(--color-primary))] px-4 py-3 text-tok-sm text-white shadow-lg">
          {toast}
        </div>
      )}

      {/* Header */}
      <header>
        <p className="text-tok-xs font-semibold uppercase tracking-widest text-[rgb(var(--color-text-muted))] mb-1">Stage 8C</p>
        <h2 className="text-tok-xl font-semibold text-[rgb(var(--color-text))]">Deal Splits</h2>
        <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">
          Configure split plans, assign positions, and validate transaction-level allocations.
          All allocation groups must sum to exactly 1.0000 before final run.
        </p>
      </header>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[rgb(var(--color-border))]">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className={[
              'px-4 py-2 text-tok-sm font-medium border-b-2 -mb-px transition-colors',
              activeTab === tab.key
                ? 'border-[rgb(var(--color-primary))] text-[rgb(var(--color-primary))]'
                : 'border-transparent text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]',
            ].join(' ')}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* TAB: Split Plans                                                    */}
      {/* ------------------------------------------------------------------ */}
      {activeTab === 'plans' && (
        <div className="flex flex-col gap-6">
          <div className="flex justify-end">
            <button
              type="button"
              className={btnPrimary}
              onClick={() => { setShowPlanForm(true); setPlanFormError(''); }}
              disabled={showPlanForm}
            >
              + New split plan
            </button>
          </div>

          {plansError && (
            <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
              {plansError}
            </div>
          )}

          {/* Create form */}
          {showPlanForm && (
            <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-4 shadow-sm">
              <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">New split plan</h3>
              {planFormError && (
                <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
                  {planFormError}
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className={labelClass}>
                  Plan code
                  <input type="text" className={inputClass} value={planForm.split_plan_code}
                    onChange={(e) => updatePlanForm('split_plan_code', e.target.value)}
                    placeholder="e.g. ENT_SPLIT_STANDARD" />
                </label>
                <label className={labelClass}>
                  Plan name
                  <input type="text" className={inputClass} value={planForm.split_plan_name}
                    onChange={(e) => updatePlanForm('split_plan_name', e.target.value)}
                    placeholder="e.g. Enterprise Standard Deal Split Plan" />
                </label>
                <label className={labelClass}>
                  Max split participants
                  <input type="number" min="2" step="1" className={inputClass}
                    value={planForm.max_split_participants}
                    onChange={(e) => updatePlanForm('max_split_participants', e.target.value)} />
                </label>
                <div className="flex flex-col gap-3 pt-2">
                  <label className="flex items-center gap-2 text-tok-sm font-medium text-[rgb(var(--color-text))] cursor-pointer">
                    <input type="checkbox" checked={planForm.requires_full_allocation}
                      onChange={(e) => updatePlanForm('requires_full_allocation', e.target.checked)} />
                    Requires full allocation (sum = 1.0000)
                  </label>
                  <label className="flex items-center gap-2 text-tok-sm font-medium text-[rgb(var(--color-text))] cursor-pointer">
                    <input type="checkbox" checked={planForm.allow_single_rep_no_split}
                      onChange={(e) => updatePlanForm('allow_single_rep_no_split', e.target.checked)} />
                    Allow single rep (no split)
                  </label>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-2">
                <button type="button" className={btnPrimary} onClick={createPlan} disabled={planSaving}>
                  {planSaving ? 'Creating…' : 'Create split plan'}
                </button>
                <button type="button" className={btnSecondary}
                  onClick={() => { setShowPlanForm(false); resetPlanForm(); }} disabled={planSaving}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Plans table */}
          <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-4 shadow-sm">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Split plans</h3>
              <button type="button" className={btnSecondary} onClick={loadPlans} disabled={plansLoading}>Refresh</button>
            </div>
            {plansLoading && <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Loading…</p>}
            {!plansLoading && plans.length === 0 && (
              <p className="text-tok-sm text-[rgb(var(--color-text-muted))] italic">
                No split plans created. Use the button above to create the first plan.
              </p>
            )}
            {plans.length > 0 && (
              <div className="overflow-x-auto">
                <table className="min-w-full text-tok-sm border-collapse">
                  <thead>
                    <tr>
                      <th className={thClass}>Plan code</th>
                      <th className={thClass}>Plan name</th>
                      <th className={thClass}>Max participants</th>
                      <th className={thClass}>Status</th>
                      <th className={thClass} aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {plans.map((plan) => {
                      const state = deriveState(plan);
                      return (
                        <tr key={plan.split_plan_id} className="hover:bg-[rgb(var(--color-surface-muted))]">
                          <td className={tdClass}>{plan.split_plan_code}</td>
                          <td className={tdClass}>{plan.split_plan_name}</td>
                          <td className={tdClass}>{plan.max_split_participants}</td>
                          <td className={tdClass}><StatusPill status={state} /></td>
                          <td className={tdClass}>
                            <button type="button" className={btnSecondary}
                              onClick={() => loadPlanDetail(plan.split_plan_id)}>
                              {state === 'draft' ? 'Review / Submit' : state === 'pending_approval' ? 'Review / Approve' : 'View'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* TAB: Position Assignments                                           */}
      {/* ------------------------------------------------------------------ */}
      {activeTab === 'assignments' && (
        <div className="flex flex-col gap-6">
          {/* Assignment form */}
          <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-4 shadow-sm">
            <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Assign split plan to position</h3>
            <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
              Only approved split plans may be assigned. One active assignment per position per fiscal period.
              Assignment also writes <code>position_calculation_config.split_plan_id</code>.
            </p>
            {assignFormError && (
              <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
                {assignFormError}
              </div>
            )}
            {approvedPlans.length === 0 && (
              <div className="rounded-lg bg-[rgb(var(--color-warning))]/10 border border-[rgb(var(--color-warning))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-warning))]">
                No approved split plans available. Approve a split plan before assigning.
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <label className={labelClass}>
                Split plan
                <select className={inputClass} value={assignForm.split_plan_id}
                  onChange={(e) => updateAssignForm('split_plan_id', e.target.value)}
                  disabled={approvedPlans.length === 0}>
                  <option value="">Select approved plan</option>
                  {approvedPlans.map((p) => (
                    <option key={p.split_plan_id} value={p.split_plan_id}>
                      {p.split_plan_name} ({p.split_plan_code})
                    </option>
                  ))}
                </select>
              </label>
              <label className={labelClass}>
                Position
                <select className={inputClass} value={assignForm.position_id}
                  onChange={(e) => updateAssignForm('position_id', e.target.value)}>
                  <option value="">Select position</option>
                  {positions.map((p) => (
                    <option key={p.position_id} value={p.position_id}>
                      {p.full_name || `Position ${p.position_id}`}
                    </option>
                  ))}
                </select>
              </label>
              <label className={labelClass}>
                Fiscal period ID
                <input type="number" className={inputClass}
                  value={assignForm.fiscal_period_id}
                  onChange={(e) => updateAssignForm('fiscal_period_id', e.target.value)}
                  placeholder={period?.fiscal_period_id ?? 'e.g. 11558'} />
              </label>
            </div>
            <div className="flex items-center gap-2">
              <button type="button" className={btnPrimary} onClick={createAssignment}
                disabled={assignSaving || approvedPlans.length === 0}>
                {assignSaving ? 'Assigning…' : 'Create assignment'}
              </button>
            </div>
          </div>

          {/* Assignments table */}
          <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-4 shadow-sm">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Current assignments</h3>
              <button type="button" className={btnSecondary} onClick={loadAssignments} disabled={assignmentsLoading}>Refresh</button>
            </div>
            {assignmentsLoading && <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Loading…</p>}
            {assignmentsError && (
              <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
                {assignmentsError}
              </div>
            )}
            {!assignmentsLoading && assignments.length === 0 && (
              <p className="text-tok-sm text-[rgb(var(--color-text-muted))] italic">No split plan assignments for this period.</p>
            )}
            {assignments.length > 0 && (
              <div className="overflow-x-auto">
                <table className="min-w-full text-tok-sm border-collapse">
                  <thead>
                    <tr>
                      <th className={thClass}>Split plan</th>
                      <th className={thClass}>Position</th>
                      <th className={thClass}>Fiscal period</th>
                      <th className={thClass}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assignments.map((a) => (
                      <tr key={a.split_plan_assignment_id} className="hover:bg-[rgb(var(--color-surface-muted))]">
                        <td className={tdClass}>{planNameById[String(a.split_plan_id)] || a.split_plan_id}</td>
                        <td className={tdClass}>{positionNameById[String(a.position_id)] || a.position_id}</td>
                        <td className={tdClass}>{a.fiscal_period_id}</td>
                        <td className={tdClass}>{a.assignment_status || 'active'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* TAB: Allocation Validation                                          */}
      {/* ------------------------------------------------------------------ */}
      {activeTab === 'allocations' && (
        <div className="flex flex-col gap-6">
          <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-4 shadow-sm">
            <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Create split allocation</h3>
            <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
              Each group of allocation rows for a credit transaction must include the primary position,
              and split percentages must sum to exactly 1.0000. Approved plan required.
            </p>

            {allocError && (
              <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
                {allocError}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className={labelClass}>
                Credit transaction ID
                <input type="number" className={inputClass}
                  value={allocForm.credit_transaction_id}
                  onChange={(e) => setAllocForm((prev) => ({ ...prev, credit_transaction_id: e.target.value }))}
                  placeholder="e.g. 90001" />
              </label>
              <label className={labelClass}>
                Split plan
                <select className={inputClass} value={allocForm.split_plan_id}
                  onChange={(e) => setAllocForm((prev) => ({ ...prev, split_plan_id: e.target.value }))}>
                  <option value="">Select approved plan</option>
                  {approvedPlans.map((p) => (
                    <option key={p.split_plan_id} value={p.split_plan_id}>
                      {p.split_plan_name} ({p.split_plan_code})
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {/* Allocation rows */}
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <h4 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Allocation rows</h4>
                <div className={[
                  'text-tok-sm font-semibold',
                  allocTotalValid
                    ? 'text-[rgb(var(--color-success))]'
                    : 'text-[rgb(var(--color-danger))]',
                ].join(' ')}>
                  Total: {allocTotal.toFixed(4)} {allocTotalValid ? '✓' : '(must equal 1.0000)'}
                </div>
              </div>

              {allocForm.rows.map((row, index) => (
                <div key={index} className="flex items-end gap-3 flex-wrap">
                  <label className={[labelClass, 'flex-1 min-w-40'].join(' ')}>
                    {index === 0 ? 'Position' : ''}
                    <select className={inputClass} value={row.position_id}
                      onChange={(e) => updateAllocRow(index, 'position_id', e.target.value)}>
                      <option value="">Select position</option>
                      {positions.map((p) => (
                        <option key={p.position_id} value={p.position_id}>
                          {p.full_name || `Position ${p.position_id}`}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className={[labelClass, 'w-32'].join(' ')}>
                    {index === 0 ? 'Split %' : ''}
                    <input type="number" min="0.0001" max="1" step="0.0001" className={inputClass}
                      value={row.split_pct}
                      onChange={(e) => updateAllocRow(index, 'split_pct', e.target.value)}
                      placeholder="0.0000" />
                  </label>
                  <label className={[labelClass, 'w-20'].join(' ')}>
                    {index === 0 ? 'Seq' : ''}
                    <input type="number" min="1" className={inputClass} value={row.split_sequence}
                      onChange={(e) => updateAllocRow(index, 'split_sequence', e.target.value)} />
                  </label>
                  {allocForm.rows.length > 2 && (
                    <button type="button" className={btnSecondary}
                      onClick={() => removeAllocRow(index)}
                      style={{ marginBottom: '0' }}>
                      Remove
                    </button>
                  )}
                </div>
              ))}

              <button type="button" className={btnSecondary} onClick={addAllocRow}>
                + Add participant
              </button>
            </div>

            <div className="flex items-center gap-2">
              <button type="button" className={btnPrimary} onClick={submitAllocation}
                disabled={allocSaving || !allocTotalValid}>
                {allocSaving ? 'Submitting…' : 'Create allocation'}
              </button>
            </div>

            {/* Result */}
            {allocResult && (
              <div className="rounded-lg bg-[rgb(var(--color-success))]/10 border border-[rgb(var(--color-success))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-success))]">
                Allocation created. Transaction {allocResult.credit_transaction_id} —
                total {allocResult.allocation_total} across {allocResult.allocations?.length} participants.
                Status: {allocResult.allocation_status}.
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
